import { useFlags } from "launchdarkly-react-client-sdk";

import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { AMBIENT_SCRIBE_CUSTOM_TEMPLATES } from "constants/feature-flags";
import FieldLabel from "features/staff-tasks-v2/drawer/staff-task-card/field-label.component";
import {
  useGetAmbientScribeTemplatesQuery
} from "services/ambient-scribe";
import type { AmbientScribeTemplate } from "services/types/ambient-scribe-template.type";

export const AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS = {
  limit: 100,
  offset: 0,
  ordering: "-pk"
};

type AmbientScribeTemplateSelectProps = {
  value: number | null;
  onChange: (id: number) => void;
  disabled?: boolean;
  showLabel?: boolean;
  /** When provided, skip the internal templates fetch (caller owns the list). */
  templates?: AmbientScribeTemplate[];
  sx?: object;
};

export default function AmbientScribeTemplateSelect({
  value,
  onChange,
  disabled = false,
  showLabel = false,
  templates: templatesProp,
  sx
}: Readonly<AmbientScribeTemplateSelectProps>) {
  const flags = useFlags();
  const isAmbientScribeCustomTemplatesEnabled =
    flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES];

  const { data: fetchedTemplates = [] } = useGetAmbientScribeTemplatesQuery(
    AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS,
    {
      refetchOnMountOrArgChange: true,
      skip: !isAmbientScribeCustomTemplatesEnabled || templatesProp != null
    }
  );

  const templates = templatesProp ?? fetchedTemplates;
  const hasTemplates = templates.length > 0;

  if (!isAmbientScribeCustomTemplatesEnabled || !hasTemplates) {
    return null;
  }

  const resolvedValue =
    value != null && templates.some((t) => t.id === value) ? value : "";

  return (
    <Stack sx={sx}>
      {showLabel && <FieldLabel label="Scribe Template" sx={{ mb: "3px" }} />}
      <FormControl
        size="small"
        sx={{ minWidth: 280, maxWidth: 400 }}
        disabled={disabled}
      >
        <Select
          id="scribe-template-select"
          value={resolvedValue}
          displayEmpty
          sx={{
            "& .MuiOutlinedInput-notchedOutline legend": {
              display: "none"
            },
            "& .MuiOutlinedInput-notchedOutline": { top: 0 }
          }}
          renderValue={(selected) =>
            !selected ? (
              <Typography
                variant="body3"
                color="text.secondary"
                component="span"
              >
                Select Scribe Template
              </Typography>
            ) : (
              <Typography variant="body3" component="span" fontWeight={700}>
                {templates.find((t) => Number(t.id) === Number(selected))
                  ?.name ?? ""}
              </Typography>
            )
          }
          onChange={(e) => {
            const next = e.target.value;
            if (next !== "") {
              onChange(Number(next));
            }
          }}
          MenuProps={{
            sx: { zIndex: 9999 },
            PaperProps: { sx: { zIndex: 9999 } }
          }}
          disabled={disabled}
        >
          <MenuItem value="" disabled>
            Select a template
          </MenuItem>
          {templates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {template.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
